import React from 'react'
import { useTranslation } from 'react-i18next'
import { CARD_CLASS } from '../utils/teamUi'
import { clampPct, modeIcon, modeLabel, profitClass, formatSigned } from '../utils/teamStats'
import { formatPrice } from '../../../utils/formatters'

/**
 * «Где играет банда» (docs/team-stats-api.md, §3, блок `modes`).
 *
 * В ответе всегда 8 режимов, отсортированных по обороту; для диаграммы берём
 * только сыгранные (`games > 0`). Полоса строки — доля режима в обороте
 * (`bet_pct`, главный показатель), доля по числу игр идёт второй строкой:
 * 50 дешёвых кейсов и один дорогой кейсбатл дают разные картины.
 * Доли округлены до целого и в сумме дают 99–101 — это нормально.
 */
const TeamModesBreakdown = ({ modes, accent }) => {
  const { t } = useTranslation()
  const played = (Array.isArray(modes) ? modes : []).filter((mode) => (mode.games ?? 0) > 0)

  return (
    <div className={`${CARD_CLASS} p-4`}>
      <h2 className="mb-3 text-sm font-bold not-italic uppercase tracking-wide text-text-secondary">
        {t('team.stats.modesTitle', 'Где играет банда')}
      </h2>

      {played.length === 0 ? (
        <p className="text-sm text-text-secondary">
          {t('team.stats.modesEmpty', 'Командных игр за период не было')}
        </p>
      ) : (
        <div className="space-y-2">
          {played.map((mode) => {
            const Icon = modeIcon(mode.kind)
            const betPct = clampPct(mode.bet_pct)

            return (
              <div
                key={mode.kind}
                className="relative overflow-hidden rounded-xl border border-white/5 bg-dark-primary p-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]"
              >
                {/* Доля в обороте — фоновая полоса цвета банды, а не отдельный прогресс-бар */}
                <div
                  className="pointer-events-none absolute inset-y-0 left-0"
                  style={{
                    width: `${betPct}%`,
                    backgroundImage: `linear-gradient(90deg, ${accent}26, transparent)`
                  }}
                />

                <div className="relative flex items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-2">
                    <Icon size={15} style={{ color: accent }} className="shrink-0" />
                    <span className="truncate text-sm font-semibold text-white">{modeLabel(mode.kind, t)}</span>
                  </div>
                  <span className="shrink-0 text-sm font-bold tabular-nums" style={{ color: accent }}>
                    {betPct}%
                  </span>
                </div>

                <div className="relative mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-text-secondary">
                  <span className="tabular-nums">
                    {t('team.stats.modeGames', '{{count}} игр · {{pct}}%', {
                      count: mode.games ?? 0,
                      pct: clampPct(mode.games_pct)
                    })}
                  </span>
                  <span className="tabular-nums">
                    {t('team.stats.modeBet', 'оборот {{bet}}', { bet: formatPrice(mode.bet ?? 0) })}
                  </span>
                  <span className={`tabular-nums ${profitClass(mode.profit)}`}>
                    {formatSigned(mode.profit ?? 0)}
                  </span>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default TeamModesBreakdown
